/**
 * 定位服务 - 支持企业微信、微信公众号、外部浏览器
 * 策略：懒授权 + 本地缓存，避免重复弹窗
 */

// 定位服务 - 支持企业微信、微信公众号、外部浏览器

// ==================== 环境检测 ====================
export const isWechat = (): boolean => {
  const ua = navigator.userAgent.toLowerCase();
  return ua.includes("micromessenger") && !ua.includes("wxwork");
};

export const isWorkWechat = (): boolean => {
  const ua = navigator.userAgent.toLowerCase();
  return ua.includes("wxwork");
};

export const isWechatEnv = (): boolean => isWechat() || isWorkWechat();

// ==================== 类型定义 ====================
export interface LocationResult {
  latitude: number;
  longitude: number;
  address?: string;
  province?: string;
  city?: string;
  district?: string;
  township?: string;   // 乡镇/街道
  street?: string;
  formattedAddress?: string;
  timestamp?: number;
}

export type LocationAuthStatus = "unknown" | "granted" | "denied" | "prompt";

// 缓存有效期（5分钟）
const CACHE_DURATION = 5 * 60 * 1000;

// 默认城市（成都）
export const DEFAULT_CITY = {
  name: "成都",
  latitude: 30.6595,
  longitude: 104.0633,
  province: "四川省",
  city: "成都市",
  district: "",
  township: "",
  address: "四川省成都市",
  formattedAddress: "四川省成都市",
};

/**
 * 使用微信 JS-SDK 获取定位
 * 适用于：企业微信、微信公众号
 */
export const getWechatLocation = (): Promise<LocationResult> => {
  return new Promise((resolve, reject) => {
    if (!window.wx) {
      reject(new Error("微信 JS-SDK 未加载"));
      return;
    }

    // 企业微信/微信使用 gcj02 坐标系
    window.wx.getLocation({
      type: "gcj02",
      success: (res: any) => {
        resolve({
          latitude: res.latitude,
          longitude: res.longitude,
        });
      },
      fail: (err: any) => {
        reject(new Error(`微信定位失败: ${err.errMsg || JSON.stringify(err)}`));
      },
    });
  });
};

/**
 * 使用 HTML5 Geolocation API 获取定位
 * 适用于：外部浏览器
 */
export const getBrowserLocation = (): Promise<LocationResult> => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("浏览器不支持地理定位"));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
      },
      (error) => {
        const errorMessages: Record<number, string> = {
          1: "用户拒绝了定位权限",
          2: "位置信息不可用",
          3: "获取位置超时",
        };
        reject(new Error(errorMessages[error.code] || "定位失败"));
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000,
      },
    );
  });
};

/**
 * 获取高德地图 Key（H5 通用）
 * 适用于：外部浏览器、微信内置浏览器等所有 H5 环境
 */
export const getAMapKey = (): string => {
  return import.meta.env.VITE_MAP_KEY_H5 || "";
};

/**
 * 获取高德地图 Web服务 Key（REST API）
 * 适用于：输入提示、逆地理编码等 Web服务 API
 */
export const getAMapWebServiceKey = (): string => {
  return import.meta.env.VITE_MAP_KEY_WEB_SERVICE || import.meta.env.VITE_MAP_KEY_H5 || "";
};

/**
 * 获取安全密钥（用于 window 对象挂载验证）
 */
export const getAMapSafeKey = (): string => {
  return import.meta.env.VITE_MAP_SAFE_KEY || "";
};

/**
 * 高德地图逆地理编码 - 将坐标转换为地址
 */
export const reverseGeocode = async (
  longitude: number,
  latitude: number,
): Promise<Partial<LocationResult>> => {
  const key = getAMapWebServiceKey();
  if (!key) {
    console.warn("高德地图 Web服务 Key 未配置");
    return {};
  }

  try {
    const response = await fetch(
      `https://restapi.amap.com/v3/geocode/regeo?key=${key}&location=${longitude},${latitude}&extensions=all`,
    );
    const data = await response.json();

    if (data.status === "1" && data.regeocode) {
      const { addressComponent, formatted_address } = data.regeocode;
      return {
        address: formatted_address,
        province: addressComponent.province,
        city: addressComponent.city,
        district: addressComponent.district,
        township: addressComponent.township || "",
        street: addressComponent.street,
        formattedAddress: formatted_address,
      };
    }
    return {};
  } catch (error) {
    console.error("逆地理编码失败:", error);
    return {};
  }
};

/** 高德 POI 输入提示单条结果 */
export interface PoiTip {
  id: string;
  /** POI 名称，如「天府广场」 */
  name: string;
  /** 详细地址，如「四川省成都市青羊区人民南路...」 */
  district: string;
  /** 所属城市（可能为空，直辖市为省级） */
  city: string;
  /** 经纬度字符串 "lng,lat"，部分 POI 可能为空 */
  location: string;
  /** 类型，如「风景名胜;公园广场」 */
  type?: string;
  /** 区域编码 */
  adcode?: string;
}

/**
 * 高德输入提示（POI 联想搜索）
 *
 * 对应 Web服务 API：https://restapi.amap.com/v3/assistant/inputtips
 * 特点：支持任意关键字的联想（图书馆、火锅、景点…），不局限于城市名
 *
 * @param keyword 用户输入的关键字
 * @param city    限定城市（可选，传城市名或 adcode 可提高准确度）
 */
export const searchPoiTips = async (
  keyword: string,
  city?: string,
): Promise<PoiTip[]> => {
  const key = getAMapWebServiceKey();
  if (!key || !keyword.trim()) return [];

  try {
    const params = new URLSearchParams({
      key,
      keywords: keyword.trim(),
      // 不限定类型，让所有 POI 都能被搜到
      datatype: "all",
    });
    if (city) params.set("city", city);

    const res = await fetch(
      `https://restapi.amap.com/v3/assistant/inputtips?${params.toString()}`,
    );
    const data = await res.json();

    if (data.status !== "1" || !Array.isArray(data.tips)) {
      console.warn("[高德输入提示] 返回异常:", data.info);
      return [];
    }

    return (data.tips as any[])
      .filter((t) => typeof t.name === "string" && t.name.trim())
      .map((t) => ({
        id: String(t.id || t.name),
        name: String(t.name).replace(/<[^>]+>/g, ""), // 去除高亮标签
        district: String(t.district ?? t.address ?? ""),
        city: typeof t.city === "string" ? t.city : "",
        location: typeof t.location === "string" ? t.location : "",
        type: typeof t.typecode === "string" ? t.typecode : undefined,
        adcode: typeof t.adcode === "string" ? t.adcode : undefined,
      }));
  } catch (error) {
    console.error("[高德输入提示] 请求失败:", error);
    return [];
  }
};

/**
 * 获取当前位置（智能选择最优方案）
 * 优先级：企业微信/微信 JS-SDK > HTML5 Geolocation
 */
export const getCurrentLocation = async (options?: {
  withAddress?: boolean;
}): Promise<LocationResult> => {
  const { withAddress = true } = options || {};

  let location: LocationResult;

  // 1. 优先尝试微信 JS-SDK（企业微信/微信公众号）
  if (isWechatEnv() && window.wx) {
    try {
      location = await getWechatLocation();
    } catch (err) {
      console.log("微信定位失败，尝试浏览器定位:", err);
      location = await getBrowserLocation();
    }
  } else {
    // 2. 外部浏览器使用 HTML5 Geolocation
    location = await getBrowserLocation();
  }

  // 3. 如果需要地址信息，进行逆地理编码
  if (withAddress) {
    try {
      const addressInfo = await reverseGeocode(
        location.longitude,
        location.latitude,
      );
      return { ...location, ...addressInfo };
    } catch (err) {
      console.error("获取地址信息失败:", err);
    }
  }

  return location;
};

// ==================== 缓存管理 ====================
const CACHE_KEY = "user_location_cache";

export const getCachedLocation = (): LocationResult | null => {
  try {
    const cached = sessionStorage.getItem(CACHE_KEY);
    if (!cached) return null;

    const data: LocationResult = JSON.parse(cached);
    // 检查缓存是否过期
    if (data.timestamp && Date.now() - data.timestamp < CACHE_DURATION) {
      return data;
    }
    return null;
  } catch {
    return null;
  }
};

export const setCachedLocation = (location: LocationResult): void => {
  sessionStorage.setItem(
    CACHE_KEY,
    JSON.stringify({ ...location, timestamp: Date.now() }),
  );
};

export const clearLocationCache = (): void => {
  sessionStorage.removeItem(CACHE_KEY);
};

// ==================== 授权状态管理 ====================

/**
 * 检查定位授权状态（外部浏览器）
 */
export const checkLocationAuth = async (): Promise<LocationAuthStatus> => {
  if (!navigator.permissions) {
    return "unknown";
  }

  try {
    const result = await navigator.permissions.query({ name: "geolocation" });
    return result.state as LocationAuthStatus;
  } catch {
    return "unknown";
  }
};

/**
 * 预授权定位 - 在合适的时机提前请求权限
 * 建议调用时机：首页加载后、用户点击"附近"等操作前
 */
export const preauthorizeLocation = async (): Promise<boolean> => {
  // 1. 先检查缓存
  const cached = getCachedLocation();
  if (cached) return true;

  // 2. 检查授权状态
  const authStatus = await checkLocationAuth();

  // 已授权，静默获取位置
  if (authStatus === "granted") {
    try {
      const location = await getCurrentLocation({ withAddress: true });
      setCachedLocation(location);
      return true;
    } catch {
      return false;
    }
  }

  // 已被拒绝，不主动弹窗
  if (authStatus === "denied") {
    return false;
  }

  // 未确定，返回 false（等待用户主动触发）
  return false;
};

// ==================== 最佳实践建议 ====================

/**
 * 获取默认城市位置（成都）
 */
export const getDefaultLocation = (): LocationResult => {
  return {
    latitude: DEFAULT_CITY.latitude,
    longitude: DEFAULT_CITY.longitude,
    province: DEFAULT_CITY.province,
    city: DEFAULT_CITY.city,
    district: DEFAULT_CITY.district,
    township: DEFAULT_CITY.township,
    address: DEFAULT_CITY.address,
    formattedAddress: DEFAULT_CITY.formattedAddress,
    timestamp: Date.now(),
  };
};

/**
 * 【推荐】获取定位的完整流程
 *
 * 使用场景：需要定位数据时调用
 *
 * 策略：
 * 1. 先读缓存 → 2. 缓存过期/不存在则获取新位置 → 3. 更新缓存
 * 4. 获取失败时返回默认城市（成都）
 *
 * 示例：
 * ```
 * // 在组件中使用
 * const location = await getLocationWithCache();
 * // 如果用户未授权，location 会返回成都的经纬度
 * ```
 */
export const getLocationWithCache = async (options?: {
  forceRefresh?: boolean;
  withAddress?: boolean;
  useDefault?: boolean;
}): Promise<LocationResult> => {
  const {
    forceRefresh = false,
    withAddress = true,
    useDefault = true,
  } = options || {};

  // 1. 强制刷新时跳过缓存
  if (!forceRefresh) {
    const cached = getCachedLocation();
    if (cached) return cached;
  }

  // 2. 获取新位置
  try {
    const location = await getCurrentLocation({ withAddress });
    setCachedLocation(location);
    return location;
  } catch (error: any) {
    console.error("获取定位失败:", error);

    // 3. 获取失败时返回默认城市（成都）
    if (useDefault) {
      console.log("使用默认城市（成都）作为兜底");
      return getDefaultLocation();
    }
    throw error;
  }
};

/**
 * 【推荐】用户主动触发定位
 *
 * 使用场景：用户点击"定位"、"附近"、"当前位置"等按钮时
 *
 * 特点：
 * - 首次会弹出授权提示
 * - 成功后会缓存结果
 * - 失败时返回友好提示
 */
export const requestLocationOnUserAction = async (): Promise<{
  success: boolean;
  location?: LocationResult;
  error?: string;
}> => {
  try {
    const location = await getCurrentLocation({ withAddress: true });
    setCachedLocation(location);
    return { success: true, location };
  } catch (error: any) {
    const message = error.message || "定位失败";
    return { success: false, error: message };
  }
};

/**
 * 【推荐】首页预加载定位
 *
 * 使用时机：App 启动后，在首页 componentDidMount 中调用
 *
 * 作用：
 * - 已授权用户：静默获取位置，后续页面直接使用缓存
 * - 未授权用户：不打扰，等待用户主动操作
 */
export const initLocationOnAppStart = async (): Promise<void> => {
  // 只在已授权情况下预加载
  const authStatus = await checkLocationAuth();
  if (authStatus === "granted") {
    const cached = getCachedLocation();
    if (!cached) {
      try {
        const location = await getCurrentLocation({ withAddress: true });
        setCachedLocation(location);
      } catch (error) {
        console.log("预加载定位失败:", error);
      }
    }
  }
};

// ==================== 微信 JS-SDK 初始化 ====================

/**
 * 初始化微信 JS-SDK（用于企业微信/微信公众号）
 */
export const initWechatSDK = async (config: {
  appId: string;
  timestamp: number;
  nonceStr: string;
  signature: string;
}): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (!window.wx) {
      reject(new Error("微信 JS-SDK 未加载"));
      return;
    }

    window.wx.config({
      ...config,
      jsApiList: ["getLocation", "openLocation"],
    });

    window.wx.ready(() => {
      resolve();
    });

    window.wx.error((err: any) => {
      reject(new Error(`微信 SDK 初始化失败: ${err.errMsg}`));
    });
  });
};

// ==================== Hook 推荐（可选）====================

/**
 * React Hook 使用示例（可在 hooks/useLocation.ts 中创建）
 *
 * ```typescript
 * export const useLocation = () => {
 *   const [location, setLocation] = useState<LocationResult | null>(null);
 *   const [loading, setLoading] = useState(false);
 *
 *   // App 启动时预加载
 *   useEffect(() => {
 *     initLocationOnAppStart().then(() => {
 *       const cached = getCachedLocation();
 *       if (cached) setLocation(cached);
 *     });
 *   }, []);
 *
 *   // 用户主动刷新
 *   const refresh = async () => {
 *     setLoading(true);
 *     const result = await requestLocationOnUserAction();
 *     setLoading(false);
 *     if (result.success) setLocation(result.location!);
 *     return result;
 *   };
 *
 *   return { location, loading, refresh };
 * };
 * ```
 */

// 类型声明
declare global {
  interface Window {
    wx: any;
  }
}
